/**
 * Phase 296.d — Parent QR-Scan Receiver
 *
 * Lives at `/scan?token=<JWT>` so the native iOS/Android camera can open it
 * directly when a parent points their phone at the coach's rotating QR.
 *
 * Flow:
 *   1. Pull `?token=` from the URL.
 *   2. Auth guard — if not logged in, bounce to `/login?next=/scan?token=...`.
 *   3. Fetch `/api/parent/children` →
 *        a. 1 child  → big "Confirm check-in for {Name}" CTA.
 *        b. 2+ kids  → quick sibling picker.
 *   4. POST `/api/attendance/qr-scan` with `{token, student_id}`.
 *   5. Massive green success state + PWA "Add to Home Screen" install nudge.
 *
 * Design is intentionally outdoor-sun friendly: white background, huge
 * typography, single dominant action color (emerald).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePwaInstall } from '@/lib/pwaInstall';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  CheckCircle2, AlertTriangle, Loader2, Users, Smartphone, Plus,
} from 'lucide-react';
import { toast } from 'sonner';

function ChildAvatar({ child, size = 'h-14 w-14' }) {
  const initials = (child?.name || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  return (
    <Avatar className={size}>
      {child?.profile_photo_url && <AvatarImage src={child.profile_photo_url} alt={child.name} className="object-cover" />}
      <AvatarFallback className="bg-emerald-100 text-emerald-800 font-bold">{initials}</AvatarFallback>
    </Avatar>
  );
}

export default function ParentScanReceiver() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { canInstall, isStandalone, isIos, promptInstall } = usePwaInstall();

  // ── Extract token from URL ───────────────────────────────────────────
  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('token') || '').trim();
  }, [location.search]);

  const [children, setChildren] = useState(null); // null = loading
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { ok, student_name, idempotent } | null
  const [error, setError] = useState(null);
  const [pwaDismissed, setPwaDismissed] = useState(false);

  // ── Auth guard — bounce to login with ?next= preserving the token ────
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const next = encodeURIComponent(`/scan?token=${encodeURIComponent(token)}`);
      navigate(`/login?next=${next}`, { replace: true });
    }
  }, [authLoading, user, token, navigate]);

  // ── Token sanity check ───────────────────────────────────────────────
  const missingToken = !token;

  // ── Fetch parent's children (only when role=parent) ──────────────────
  useEffect(() => {
    if (!user || authLoading) return;
    if (user.role === 'parent') {
      api.get('/parent/children')
        .then((r) => setChildren(Array.isArray(r.data) ? r.data : []))
        .catch(() => setChildren([]));
    } else if (user.role === 'student') {
      // Student self check-in — derive the "child" entry from their own user record
      setChildren([{ id: user.id, name: user.name, profile_photo_url: user.profile_photo_url }]);
    } else {
      // Coaches/admins/owners shouldn't be hitting this page.
      setChildren([]);
    }
  }, [user, authLoading]);

  // ── Submit the QR scan ───────────────────────────────────────────────
  const submitCheckIn = useCallback(async (studentId) => {
    if (!token || !studentId) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.post('/attendance/qr-scan', { token, student_id: studentId });
      setResult({
        ok: true,
        student_name: r.data?.student_name,
        idempotent: !!r.data?.idempotent,
      });
      toast.success(r.data?.idempotent ? 'Already checked in earlier today' : `${r.data?.student_name} checked in`);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not check in — try again or ask the coach.';
      setError(detail);
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  }, [token]);

  // Auto-submit the single-child case once children load (saves a tap)
  // BUT only the very first time — never auto-retry on error or after success.
  const [autoSubmitted, setAutoSubmitted] = useState(false);
  useEffect(() => {
    if (autoSubmitted || result || error || submitting) return;
    if (!children || children.length !== 1) return;
    if (!token) return;
    setAutoSubmitted(true);
    submitCheckIn(children[0].id);
  }, [children, autoSubmitted, result, error, submitting, token, submitCheckIn]);

  // ── PWA install handler ──────────────────────────────────────────────
  const handlePwaInstall = useCallback(async () => {
    const ok = await promptInstall();
    if (!ok) toast.info('Install cancelled — you can re-trigger any time from the menu.');
  }, [promptInstall]);

  // ────────────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────────────
  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-slate-600">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (missingToken) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center" data-testid="scan-missing-token">
        <AlertTriangle className="h-12 w-12 text-rose-500 mb-3" />
        <h1 className="text-xl font-bold text-slate-900 mb-1">Invalid scan link</h1>
        <p className="text-sm text-slate-600 max-w-sm">This page expects a `?token=` query — ask your coach to show a fresh QR code.</p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-white text-slate-900 flex flex-col"
      data-testid="parent-scan-receiver"
      style={{ colorScheme: 'light' }}
    >
      {/* ───── Header — minimal, just the academy mark ───── */}
      <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-center">
        <p className="text-sm font-semibold text-emerald-700 tracking-wide" data-testid="scan-header">Out-Play · Check-in</p>
      </header>

      <main className="flex-1 px-5 py-6 flex flex-col items-center justify-center max-w-md w-full mx-auto space-y-5">
        {/* ───── SUCCESS STATE ───── */}
        {result && (
          <div className="w-full text-center" data-testid="scan-success-state">
            <div className="mx-auto w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-12 w-12 text-emerald-600" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-display font-bold text-emerald-700 mb-1" data-testid="scan-success-headline">
              {result.idempotent ? 'Already checked in' : 'Checked in!'}
            </h1>
            <p className="text-sm text-slate-600 mb-1">
              <span className="font-semibold text-slate-900">{result.student_name}</span> is marked present for today.
            </p>
            {!result.idempotent && (
              <p className="text-xs text-slate-500">
                Your coach will get a notification — enjoy practice. ⚽
              </p>
            )}

            {/* PWA Install Nudge — show only when actually installable AND not already standalone */}
            {!pwaDismissed && !isStandalone && (canInstall || isIos) && (
              <div className="mt-6 rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-4 text-left" data-testid="scan-pwa-install-card">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <Smartphone className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">
                      Install Out-Play for faster check-ins
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                      One tap from your home screen — no more digging through camera & browser.
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                      {canInstall && !isIos && (
                        <Button
                          size="sm"
                          className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                          onClick={handlePwaInstall}
                          data-testid="scan-pwa-install-btn"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" /> Add to Home Screen
                        </Button>
                      )}
                      {isIos && (
                        <p className="text-[11px] text-slate-500" data-testid="scan-pwa-ios-hint">
                          On iPhone: tap <span className="font-mono">Share</span> → <span className="font-semibold">Add to Home Screen</span>.
                        </p>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-xs text-slate-500"
                        onClick={() => setPwaDismissed(true)}
                        data-testid="scan-pwa-dismiss-btn"
                      >
                        Not now
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Button
              variant="outline"
              size="sm"
              className="mt-6 h-9 text-xs"
              onClick={() => navigate(user.role === 'parent' ? '/parent' : '/student-portal')}
              data-testid="scan-back-to-portal-btn"
            >
              Back to portal
            </Button>
          </div>
        )}

        {/* ───── ERROR STATE ───── */}
        {error && !result && (
          <div className="w-full text-center" data-testid="scan-error-state">
            <div className="mx-auto w-20 h-20 rounded-full bg-rose-100 flex items-center justify-center mb-4">
              <AlertTriangle className="h-12 w-12 text-rose-600" />
            </div>
            <h1 className="text-xl font-bold text-rose-700 mb-1" data-testid="scan-error-headline">Couldn&apos;t check in</h1>
            <p className="text-sm text-slate-600 mb-4 max-w-xs mx-auto">{error}</p>
            {children && children.length > 0 && (
              <Button
                onClick={() => { setError(null); if (children.length === 1) submitCheckIn(children[0].id); }}
                className="h-10 text-sm bg-emerald-600 hover:bg-emerald-700"
                data-testid="scan-error-retry-btn"
              >
                Try again
              </Button>
            )}
          </div>
        )}

        {/* ───── PICKER STATE (multi-child) or LOADING (single-child while auto-submitting) ───── */}
        {!result && !error && children && children.length > 1 && (
          <div className="w-full" data-testid="scan-picker-state">
            <div className="text-center mb-5">
              <Users className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
              <h1 className="text-xl font-bold text-slate-900">Who&apos;s checking in?</h1>
              <p className="text-sm text-slate-600 mt-1">Tap your child to mark them present.</p>
            </div>
            <div className="space-y-2">
              {children.map((c) => (
                <button
                  key={c.id}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 transition-colors text-left disabled:opacity-50"
                  onClick={() => submitCheckIn(c.id)}
                  disabled={submitting}
                  data-testid={`scan-child-row-${c.id}`}
                >
                  <ChildAvatar child={c} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{c.name}</p>
                    <p className="text-xs text-slate-500 truncate">{c.sport || c.batch || c.academy_name || 'Student'}</p>
                  </div>
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600" /> : <CheckCircle2 className="h-5 w-5 text-slate-300" />}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ───── LOADING (single child auto-submitting OR fetching children) ───── */}
        {!result && !error && (children === null || (children && children.length === 1 && submitting)) && (
          <div className="text-center text-slate-600" data-testid="scan-loading-state">
            <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mx-auto mb-3" />
            <p className="text-sm">Checking in {children && children[0]?.name ? children[0].name : ''}…</p>
          </div>
        )}

        {/* ───── EMPTY (no children linked) ───── */}
        {!result && !error && children && children.length === 0 && (
          <div className="text-center" data-testid="scan-no-children-state">
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <h1 className="text-lg font-bold text-slate-900 mb-1">No students linked to your account</h1>
            <p className="text-sm text-slate-600 max-w-xs mx-auto">
              Your parent account isn&apos;t linked to a student yet. Ask the academy front-desk to link your child.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
